'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useFirebaseService } from '@/hooks/useFirebaseService';
import { useAuth } from '@/contexts/authContext';
import { TopStoriesCard } from './top-stories-card';
import { CarouselDots } from './carousel-dots';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import SummaryDetailInfo from '@/functions/src/entities/summaryDetailInfo';
import { BriefingPeriod } from './market-briefing-types';

interface TopStoriesProps {
  activePeriod?: BriefingPeriod;
  className?: string;
}

const AUTO_ROTATION_INTERVAL = 4000; // 4 seconds

// Fixed height for the stories container to prevent layout shift
const STORIES_CONTAINER_HEIGHT = 'min-h-[220px] md:min-h-[200px]';

// Helper function to add ordinal suffix to day
function addOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return day + 'th';
  switch (day % 10) {
    case 1: return day + 'st';
    case 2: return day + 'nd';
    case 3: return day + 'rd';
    default: return day + 'th';
  }
}

// Convert various date formats to Date object
function toDate(value: any): Date {
  if (!value) return new Date();

  // Firebase Timestamp has toDate() method
  if (value && typeof value.toDate === 'function') {
    return value.toDate();
  }

  // Already a Date
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? new Date() : value;
  }

  // String or number
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  return new Date();
}

function formatDate(dateValue: any): string {
  const date = toDate(dateValue);
  const day = date.getDate();
  const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
  return `${month} ${addOrdinalSuffix(day)}`;
}

export function TopStories({ activePeriod = 'morning', className = '' }: TopStoriesProps) {
  const [stories, setStories] = useState<SummaryDetailInfo[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [autoRotationPaused, setAutoRotationPaused] = useState(false);
  const rotationTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { getMultipleMarketNewsSnapshot } = useFirebaseService();
  const { userInfo } = useAuth();

  // Fetch market news
  useEffect(() => {
    setIsLoading(true);

    const handleData = (items: SummaryDetailInfo[]) => {
      setStories(items);
      setIsLoading(false);
    };

    const handleError = (error: any) => {
      console.error('Error fetching top stories:', error);
      setIsLoading(false);
    };

    const unsubscribe = getMultipleMarketNewsSnapshot(4, handleData, handleError, userInfo);

    return () => unsubscribe();
  }, [userInfo, getMultipleMarketNewsSnapshot]);

  // Auto-rotation
  useEffect(() => {
    if (stories.length <= 1 || autoRotationPaused) {
      if (rotationTimerRef.current) {
        clearInterval(rotationTimerRef.current);
        rotationTimerRef.current = null;
      }
      return;
    }

    rotationTimerRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % stories.length);
    }, AUTO_ROTATION_INTERVAL);

    return () => {
      if (rotationTimerRef.current) {
        clearInterval(rotationTimerRef.current);
        rotationTimerRef.current = null;
      }
    };
  }, [stories.length, autoRotationPaused]);

  const handleStoryChange = useCallback((index: number) => {
    if (index >= 0 && index < stories.length) {
      setActiveIndex(index);
      setAutoRotationPaused(true);
      setTimeout(() => setAutoRotationPaused(false), AUTO_ROTATION_INTERVAL * 2);
    }
  }, [stories.length]);

  const handlePrev = useCallback(() => {
    handleStoryChange((activeIndex - 1 + stories.length) % stories.length);
  }, [activeIndex, stories.length, handleStoryChange]);

  const handleNext = useCallback(() => {
    handleStoryChange((activeIndex + 1) % stories.length);
  }, [activeIndex, stories.length, handleStoryChange]);

  // Get period badge label
  const periodLabels: Record<BriefingPeriod, string> = {
    morning: 'Morning Edition',
    midday: 'Midday Edition',
    evening: 'Evening Edition'
  };

  // Section header component for consistency
  const SectionHeader = () => (
    <div className="flex justify-between items-center mb-5">
      <div className="flex items-center gap-3">
        <h2 className="font-display text-lg font-semibold tracking-tight text-[#e6edf3]">
          Top Stories
        </h2>
        <div className="h-4 w-px bg-slate-700/50" />
        <span className="section-label text-[11px]">Latest</span>
      </div>
      <span className="text-[11px] font-medium bg-gradient-to-r from-[#d4a853]/20 to-[#b8923f]/20 text-[#d4a853] px-3 py-1.5 rounded-full border border-[#d4a853]/20">
        {periodLabels[activePeriod]}
      </span>
    </div>
  );

  if (isLoading) {
    return (
      <section className={cn('card-surface p-6', className)}>
        <SectionHeader />
        <div className={cn('animate-pulse', STORIES_CONTAINER_HEIGHT)}>
          <div className="h-5 bg-slate-700/30 rounded w-1/4 mb-4" />
          <div className="h-6 bg-slate-700/30 rounded w-3/4 mb-3" />
          <div className="h-4 bg-slate-700/30 rounded w-full mb-2" />
          <div className="h-4 bg-slate-700/30 rounded w-5/6" />
        </div>
      </section>
    );
  }

  if (stories.length === 0) {
    return (
      <section className={cn('card-surface p-6', className)}>
        <SectionHeader />
        <div className={cn('flex items-center justify-center text-[#8b949e] italic', STORIES_CONTAINER_HEIGHT)}>
          No stories available at this time.
        </div>
      </section>
    );
  }

  const currentStory = stories[activeIndex];
  const storyDate = formatDate(currentStory.createdOn);

  return (
    <section
      className={cn('card-surface p-6 relative', className)}
      onMouseEnter={() => {
        setAutoRotationPaused(true);
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setAutoRotationPaused(false);
        setIsHovering(false);
      }}
    >
      {/* Header */}
      <SectionHeader />

      {/* Story Card Container - Fixed Height */}
      <div className={STORIES_CONTAINER_HEIGHT}>
        <TopStoriesCard
          date={storyDate}
          title={currentStory.summaryHeader || 'Market Update'}
          body={currentStory.shortSummary || 'No summary available.'}
        />
      </div>

      {/* Navigation Arrows - Show on hover when multiple stories */}
      {stories.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className={cn(
              'absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[#0d1117]/90 border border-slate-700/50 hover:border-[#d4a853]/30 hover:bg-[#161b22] transition-all duration-300 z-10',
              isHovering ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
            )}
            aria-label="Previous story"
          >
            <ChevronLeft size={18} className="text-[#8b949e]" />
          </button>
          <button
            onClick={handleNext}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[#0d1117]/90 border border-slate-700/50 hover:border-[#d4a853]/30 hover:bg-[#161b22] transition-all duration-300 z-10',
              isHovering ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
            )}
            aria-label="Next story"
          >
            <ChevronRight size={18} className="text-[#8b949e]" />
          </button>
        </>
      )}

      {/* Carousel Dots - Hidden per UX requirements */}
      {/* <CarouselDots
        total={stories.length}
        activeIndex={activeIndex}
        onDotClick={handleStoryChange}
        className="mt-5"
      /> */}
    </section>
  );
}
